/**
 * flashcards.js — Flashcard public shell.
 *
 * Owns: window note-bridge, boot overlay bindings, migration, subject helpers.
 * Does not own: study overlay (flashcard-study.js) or hub CRUD
 * (flashcard-hub-screen.js).
 *
 * Study and hub load on first use via import() so students who never open
 * either do not download them. The study overlay is reachable from the
 * subject hub and from a note without visiting #flashcards, which is why
 * overlay maps bind at boot and lazy-delegate into the study module.
 *
 * Depends on: storage.js, notes.js (nfpeEditingId getter, loadNotes).
 */

// ── Migration ──────────────────────────────────────────────────────────────────

/**
 * One-time migration of note-embedded flashcards to standalone decks.
 * Guards with a localStorage flag — safe to call repeatedly.
 */
function migrateNoteFlashcardsToDecks() {
    if (localStorage.getItem('productivity_decks_migrated') === '1') return;

    const notes    = Storage.loadNotes();
    const toMigrate = notes.filter(n => Array.isArray(n.flashcards) && n.flashcards.length > 0);
    if (toMigrate.length === 0) {
        localStorage.setItem('productivity_decks_migrated', '1');
        return;
    }

    // Avoid duplicating decks that already exist (e.g. from nfpeGenerateFlashcards)
    const existingDecks = Storage.loadDecks();
    const existingNoteIds = new Set(existingDecks.map(d => d.sourceNoteId).filter(Boolean));

    const newDecks = [...existingDecks];
    let created = 0;
    for (const note of toMigrate) {
        if (existingNoteIds.has(note.id)) continue;
        newDecks.push({
            id:           Date.now().toString() + Math.random().toString(36).slice(2, 6) + created,
            name:         note.title || 'Untitled Deck',
            description:  'Migrated from note',
            subjectId:    note.subjectId || null,
            category:     note.category  || null,
            sourceNoteId: note.id,
            cards:        note.flashcards.map(c => ({ ...c })),
            createdAt:    note.createdAt || new Date().toISOString(),
            updatedAt:    note.updatedAt || new Date().toISOString()
        });
        created++;
    }

    if (created > 0) Storage.saveDecks(newDecks);
    localStorage.setItem('productivity_decks_migrated', '1');
    if (created > 0 && typeof showNotification === 'function') {
        showNotification(`${created} flashcard deck${created > 1 ? 's' : ''} moved to the Flashcard Hub`, 'success');
    }
}

// ── Editor AI bar ──────────────────────────────────────────────────────────────

function nfpeUpdateFlashcardBar() {
    const btn      = document.getElementById('nfpeFlashcardBtn');
    const label    = document.getElementById('nfpeFlashcardBtnLabel');
    const studyBtn = document.getElementById('nfpeStudyBtn');
    const statusEl = document.getElementById('nfpeFcStatus');
    if (!btn) return;

    if (!nfpeEditingId) {
        btn.disabled = true;
        btn.title    = 'Save the note first to generate flashcards';
        if (label)    label.textContent      = 'Flashcards';
        if (studyBtn) studyBtn.style.display = 'none';
        if (statusEl) statusEl.textContent   = '';
        return;
    }

    btn.disabled = false;
    btn.title    = 'Generate flashcards from this note';

    // Prefer deck cards if a deck exists for this note
    const deck  = Storage.getDeckByNoteId(nfpeEditingId);
    const cards = deck ? Storage.getDeckCards(deck.id) : Storage.getFlashcards(nfpeEditingId);

    if (cards.length === 0) {
        if (label)    label.textContent      = 'Flashcards';
        if (studyBtn) studyBtn.style.display = 'none';
        if (statusEl) statusEl.textContent   = '';
        return;
    }

    const gotIt = cards.filter(c => c.reviewStatus === 'got-it').length;
    const again = cards.filter(c => c.reviewStatus === 'review-again').length;
    const isNew = cards.filter(c => c.reviewStatus === 'new').length;
    if (label)    label.textContent      = `${cards.length} flashcards`;
    if (studyBtn) studyBtn.style.display = '';
    if (statusEl) statusEl.textContent   = `${gotIt} ✓  ${again} ↻  ${isNew} new`;
}

// ── Generation (from note editor) ─────────────────────────────────────────────

async function nfpeGenerateFlashcards() {
    if (!nfpeEditingId) {
        showNotification('Save the note first, then generate flashcards.', 'error');
        return;
    }
    if (!window.Meneer) {
        showNotification('Meneer is not available.', 'error');
        return;
    }

    const rawHtml = document.getElementById('nfpeEditor').innerHTML;
    const _tmpDiv = document.createElement('div');
    _tmpDiv.innerHTML = rawHtml;
    const plain = (_tmpDiv.textContent || _tmpDiv.innerText || '').trim();

    if (plain.length < 30) {
        showNotification('Add some content to the note before generating flashcards.', 'error');
        return;
    }

    const btn     = document.getElementById('nfpeFlashcardBtn');
    const label   = document.getElementById('nfpeFlashcardBtnLabel');
    const spinner = document.getElementById('nfpeFlashcardSpinner');
    btn.disabled  = true;
    if (label)   label.style.display   = 'none';
    if (spinner) spinner.style.display = '';

    const system =
        'You are a flashcard generator. Read the note content and return a JSON array of flashcard objects. ' +
        'Each object has exactly two fields: "front" (a term, concept, or question) and "back" (the definition or answer). ' +
        'Return a maximum of 12 flashcards. Focus on the most important concepts. ' +
        'Return ONLY a valid JSON array — no explanation, no markdown fences, no extra text. ' +
        'Example: [{"front":"What is photosynthesis?","back":"The process plants use to convert sunlight into glucose."}]';

    const noteTitle = document.getElementById('nfpeTitle').value.trim();
    const user = `Note title: ${noteTitle}\n\nNote content:\n${plain.substring(0, 3000)}`;

    let raw;
    try {
        raw = await Meneer.callMeneer(system, user, 1200);
    } catch (callErr) {
        console.error('[Flashcards] callMeneer threw:', callErr);
        btn.disabled = false;
        if (label)   label.style.display   = '';
        if (spinner) spinner.style.display = 'none';
        showNotification('Meneer encountered an error. Check the console for details.', 'error');
        return;
    }

    btn.disabled = false;
    if (label)   label.style.display   = '';
    if (spinner) spinner.style.display = 'none';

    if (!raw) {
        showNotification('Meneer could not generate flashcards. Try again.', 'error');
        return;
    }

    let parsed;
    try {
        let clean = raw.trim();
        clean = clean.replace(/^```(?:json)?\s*/i, '');
        clean = clean.replace(/\s*```$/i, '').trim();
        if (!clean.startsWith('[')) {
            const start = clean.indexOf('[');
            const end   = clean.lastIndexOf(']');
            if (start !== -1 && end !== -1 && end > start) clean = clean.slice(start, end + 1);
        }
        parsed = JSON.parse(clean);
        if (!Array.isArray(parsed)) throw new Error('Not an array');
    } catch (parseErr) {
        console.error('[Flashcards] JSON parse failed:', parseErr, '\nRaw:', raw);
        showNotification('Meneer returned an unexpected format. Try again.', 'error');
        return;
    }

    const now = new Date().toISOString();
    const flashcards = parsed.slice(0, 12).map(fc => ({
        id:           Date.now().toString() + Math.random().toString(36).slice(2, 6),
        front:        String(fc.front || '').trim(),
        back:         String(fc.back  || '').trim(),
        reviewStatus: 'new',
        createdAt:    now
    })).filter(fc => fc.front && fc.back);

    if (flashcards.length === 0) {
        showNotification('No valid flashcards found in the response. Try again.', 'error');
        return;
    }

    const savedNoteId = nfpeEditingId;

    // Keep backward-compat: also save to note object
    Storage.saveFlashcards(savedNoteId, flashcards);

    // Create or update the standalone deck linked to this note
    const existingDeck = Storage.getDeckByNoteId(savedNoteId);
    if (existingDeck) {
        Storage.saveDeckCards(existingDeck.id, flashcards);
        Storage.updateDeck(existingDeck.id, { updatedAt: now });
    } else {
        const note = Storage.loadNotes().find(n => n.id === savedNoteId);
        Storage.createDeck({
            name:         noteTitle || (note ? note.title : 'Untitled Deck'),
            description:  'Generated from note',
            subjectId:    (note && note.subjectId) || null,
            category:     (note && note.category)  || null,
            sourceNoteId: savedNoteId,
            cards:        flashcards
        });
    }

    nfpeUpdateFlashcardBar();
    loadNotes();
    showNotification(`${flashcards.length} flashcards generated! Opening study mode…`, 'success');
    setTimeout(() => openFlashcardStudy(savedNoteId), 800);
}

/**
 * Backward-compat wrapper: open study for a note's associated deck.
 * Falls back to legacy note-embedded flashcards if no deck exists yet.
 * @param {string} [noteId]
 */
function openFlashcardStudy(noteId) {
    const id = noteId || nfpeEditingId;
    if (!id) return;

    const deck = Storage.getDeckByNoteId(id);
    if (deck) {
        openDeckStudy(deck.id, 'notes');
        return;
    }

    // Legacy path: note still has embedded flashcards but no deck yet
    const cards = Storage.getFlashcards(id);
    if (cards.length === 0) {
        showNotification('No flashcards found for this note. Generate some first.', 'error');
        return;
    }

    // Auto-create a deck so future visits use the new path
    const note = Storage.loadNotes().find(n => n.id === id);
    const newDeck = Storage.createDeck({
        name:         note ? note.title : 'Untitled Deck',
        description:  'Migrated from note',
        subjectId:    (note && note.subjectId) || null,
        category:     (note && note.category)  || null,
        sourceNoteId: id,
        cards:        cards
    });
    openDeckStudy(newDeck.id, 'notes');
}

// These previously read localStorage['studymeneer_subjects'] directly, duplicating
// the Subjects module's private storage key. AGENTS.md §5 makes subjects the
// organising spine and subjects.js the single source of truth; a key or shape
// change there would have broken flashcards silently. They now go through the
// module and fall back to the raw key only if it has not loaded yet — module
// scripts are deferred, so on a cold start something could in principle call these
// before subjects.js has evaluated.
function _readSubjects() {
    if (window.Subjects && typeof window.Subjects.getAll === 'function') {
        return window.Subjects.getAll();
    }
    try {
        const raw = localStorage.getItem('studymeneer_subjects');
        return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
}

function _getSubjectName(subjectId) {
    if (!subjectId) return null;
    const s = _readSubjects().find(s => s.id === subjectId);
    return s ? (s.name || null) : null;
}

function _getSubjectColor(subjectId) {
    if (!subjectId) return null;
    const s = _readSubjects().find(s => s.id === subjectId);
    // subjects.js writes `colour`; `color` is tolerated for older records.
    return (s && (s.color || s.colour)) ? (s.color || s.colour) : null;
}

function _getAllSubjects() {
    return _readSubjects();
}

function _escHtml(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

async function _ensureFcOverlay() {
    return !!(await window.Overlays?.ensure('flashcards'));
}

let _studyMod = null;
let _hubMod = null;

async function ensureStudy() {
    if (!_studyMod) {
        try {
            _studyMod = await import('./flashcard-study.js?v=3');
        } catch (err) {
            _studyMod = null;
            throw err;
        }
    }
    return _studyMod;
}

async function ensureHub() {
    if (!_hubMod) {
        try {
            _hubMod = await import('./flashcard-hub-screen.js?v=3');
        } catch (err) {
            _hubMod = null;
            throw err;
        }
    }
    return _hubMod;
}

async function openDeckStudy(deckId, origin) {
    if (!await _ensureFcOverlay()) return;
    const study = await ensureStudy();
    return study.openDeckStudy(deckId, origin);
}

async function closeFlashcardStudy() {
    const study = await ensureStudy();
    return study.closeFlashcardStudy();
}

async function fchStudyDeckById(deckId) {
    return openDeckStudy(deckId, 'hub');
}

async function loadFlashcardHub() {
    migrateNoteFlashcardsToDecks();
    const hub = await ensureHub();
    return hub.loadFlashcardHub();
}

async function fchOpenDeckDetail(deckId) {
    if (!await _ensureFcOverlay()) return;
    const hub = await ensureHub();
    return hub.fchOpenDeckDetail(deckId);
}

async function fchCreateDeckModal(deckId) {
    if (!await _ensureFcOverlay()) return;
    const hub = await ensureHub();
    return hub.fchCreateDeckModal(deckId);
}

async function fchOpenCardModal(cardId, deckId) {
    if (!await _ensureFcOverlay()) return;
    const hub = await ensureHub();
    return hub.fchOpenCardModal(cardId, deckId);
}

// ── Delegated event handling (ROADMAP_HANDOVER §2.1) ──────────────────────────

/**
 * Everything this module owns that lives OUTSIDE the section layer: the deck
 * detail overlay, the deck and card modals, and the study overlay. All four are
 * static markup in the overlay partial, so they bind to document rather than a
 * root.
 *
 * A document listener sees the whole page, so these names share one namespace
 * with every other overlay map in the app. `fch-` is the hub's prefix and `fc-`
 * the study session's; tests/partials.test.js fails on a duplicate across
 * document-level maps.
 */
const FC_OVERLAY_CLICK_ACTIONS = {
    'edit-card': async el => (await ensureHub()).fchOpenCardModal(el.dataset.cardId, el.dataset.deckId),
    'delete-card': async el => (await ensureHub()).fchDeleteCard(el.dataset.deckId, el.dataset.cardId),

    // Deck detail overlay
    'fch-close-detail': async () => (await ensureHub()).fchCloseDeckDetail(),
    // Explicitly null, not absent: fchOpenCardModal(cardId, deckId) treats a
    // null first argument as "new card", and the inline call said so.
    'fch-add-card': async () => (await ensureHub()).fchOpenCardModal(null),
    'fch-ai-generate': async () => (await ensureHub()).fchAIGenerateForDeck(),
    'fch-study-deck': async () => (await ensureHub()).fchStudyDeck(),

    // Deck modal
    'fch-close-deck-modal': async () => (await ensureHub()).fchCloseDeckModal(),
    'fch-source': async el => (await ensureHub()).fchSelectSource(el.dataset.source),
    'fch-save-deck': async () => (await ensureHub()).fchSaveDeck(),

    // Card editor modal
    'fch-close-card-modal': async () => (await ensureHub()).fchCloseCardModal(),
    'fch-save-card': async () => (await ensureHub()).fchSaveCard(),

    // Study overlay
    'fc-close-study': async () => (await ensureStudy()).closeFlashcardStudy(),
    'fc-toggle': async () => (await ensureStudy())._fcToggle(),
    'fc-flip': async () => (await ensureStudy())._fcFlip(),
    'fc-mark': async el => (await ensureStudy())._fcMark(el.dataset.grade),
    'fc-study-again': async () => (await ensureStudy())._fcStudyAgain(),
};

/**
 * The two source pickers are <select> elements. A form control wired to a click
 * action never fires, and it fails silently — which is why the guard classifies
 * these two event types and nothing else.
 */
const FC_OVERLAY_CHANGE_ACTIONS = {
    'fch-autofill': async el => (await ensureHub()).fchAutoFillDeckName(el.dataset.source),
};

let _fcOverlayBound = false;

function _fcDelegate(root, label, actions, type = 'click') {
    root.addEventListener(type, e => {
        const el = e.target.closest('[data-action]');
        if (!el || !root.contains(el)) return;
        const action = actions[el.dataset.action];
        if (!action) return;
        try {
            action(el, e);
        } catch (err) {
            console.error(`${label} ${type} action "${el.dataset.action}" failed:`, err);
        }
    });
}

/**
 * Bound at BOOT, from app.js, and that is a change from binding on navigation.
 *
 * The study overlay is reachable without ever opening the Flashcard Hub —
 * fchStudyDeckById() opens it straight from the subject hub, and
 * openFlashcardStudy() from a note. Left inside loadFlashcardHub(), every
 * control in the study session would have rendered and done nothing for a
 * student who arrived that way.
 */
function initFlashcardOverlayActions() {
    if (_fcOverlayBound) return;
    _fcOverlayBound = true;
    _fcDelegate(document, '[Flashcards overlay]', FC_OVERLAY_CLICK_ACTIONS);
    _fcDelegate(document, '[Flashcards overlay]', FC_OVERLAY_CHANGE_ACTIONS, 'change');

    // ─── Swipe to rate ────────────────────────────────────────────────────────
    // The study card is #fcCard.sm-fc-card (partials/overlays/flashcards.html:124).
    // An earlier pass matched '#fcStudyCard, .fc-card, .fc-study-card' — none of
    // which exist — so closest() returned null on every touch and the gesture
    // never ran. A three-name OR and a `typeof` guard cannot fail loudly, which
    // is why it read as working; both are gone.
    //
    // The swipe dispatches through the existing grade button rather than calling
    // _fcMark directly. Every control here runs on delegated data-action
    // (AGENTS.md SKILL 5), so clicking the button reuses the exact path a tap
    // takes — nothing to keep in sync when the scheduling algorithm changes.
    let _swipeX = null;
    let _swipeY = 0;

    document.addEventListener('touchstart', (e) => {
        if (!e.target.closest('#fcCard')) return;
        _swipeX = e.touches[0].clientX;
        _swipeY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
        if (_swipeX === null || !e.target.closest('#fcCard')) return;
        const diffX = e.changedTouches[0].clientX - _swipeX;
        const diffY = e.changedTouches[0].clientY - _swipeY;
        // null, not 0: a swipe starting at clientX 0 is a real swipe, and the
        // falsy check discarded it.
        _swipeX = null;

        if (Math.abs(diffX) <= 50 || Math.abs(diffX) <= Math.abs(diffY)) return;

        // Rating a face-down card would score a question the student has not
        // seen the answer to, so the gesture only applies once flipped.
        const card = document.getElementById('fcCard');
        if (card && !card.classList.contains('flipped')) return;

        const grade = diffX > 0 ? 'easy' : 'again';
        document.querySelector(`.fc-grade-btn[data-grade="${grade}"]`)?.click();
    }, { passive: true });
}


// ── ES module surface (roadmap §3 & Track C) ──────────────────────────────────
// Only verified cross-module bindings are published to window.
// Deck CRUD and study session interactions are handled via delegated event maps.
// From: node scripts/required-globals.js flashcards.js --names
const _flashcardsPublicApi = {
    closeFlashcardStudy,
    fchStudyDeckById,
    initFlashcardOverlayActions,
    loadFlashcardHub,
    nfpeGenerateFlashcards,
    nfpeUpdateFlashcardBar,
    openFlashcardStudy
};

export const Flashcards = _flashcardsPublicApi;
export {
    migrateNoteFlashcardsToDecks, _escHtml, _getSubjectName, _getSubjectColor,
    _getAllSubjects, loadFlashcardHub, openDeckStudy
};

if (typeof window !== 'undefined') Object.assign(window, _flashcardsPublicApi);
